import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { ASSETS } from "@/constants/site";
import { ArrowRight } from "lucide-react";

export default function Fleet() {
  const { t } = useLang();
  const images = [
    ASSETS.kawasaki,
    ASSETS.cat349F,
    ASSETS.catDozerLineup,
    ASSETS.mackTrucks,
    ASSETS.cat966M,
    ASSETS.cumminsGen,
    ASSETS.catDeere,
    ASSETS.catDeere,
    ASSETS.altecWinter,
    ASSETS.altecChevy,
    ASSETS.generator,
    ASSETS.mackFuel,
    ASSETS.waterousGen,
    ASSETS.mackGraniteService,
    ASSETS.roadtecShuttle,
    ASSETS.roadtecRX900,
    ASSETS.catGenEnclosed,
    ASSETS.catGenOpen,
    ASSETS.catGenSkid,
  ];

  const isAvailable = (tag) => tag === "Available" || tag === "Disponible";

  return (
    <section id="fleet" className="py-24 md:py-32 bg-[#0A0A0A] border-t border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="section-label mb-5">{t.fleet.label}</div>
            <h2 className="font-heading uppercase text-white text-4xl sm:text-5xl md:text-6xl leading-[1.05]">
              {t.fleet.title}
            </h2>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-3 text-[#F5B800] hover:text-white text-xs uppercase tracking-[0.25em] font-bold transition-colors"
          >
            {t.fleet.cta}
            <ArrowRight size={16} />
          </a>
        </div>

        {/* Uniform 3-col catalog grid: image on top, description in its own frame below */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.fleet.cards.map((c, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col bg-[#0F0F0F] border border-white/10 hover:border-[#F5B800]/40 transition-colors duration-300 group"
            >
              {/* Image */}
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={images[i]}
                  alt={c.t}
                  className="absolute inset-0 w-full h-full object-cover machine-img"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1.5 ${
                      isAvailable(c.tag)
                        ? "bg-[#F5B800] text-black"
                        : "bg-black/80 text-[#F5B800] border border-[#F5B800]/50"
                    }`}
                  >
                    {c.tag}
                  </span>
                </div>
              </div>

              {/* Text frame below */}
              <div className="p-5 md:p-6 border-t border-white/10 flex-1 flex flex-col">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#F5B800] font-bold">
                  {c.d}
                </div>
                <h3 className="mt-2 font-heading uppercase text-white text-xl md:text-2xl leading-tight">
                  {c.t}
                </h3>
                {c.desc && (
                  <p className="mt-3 text-neutral-400 text-xs leading-relaxed">
                    {c.desc}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
